import React from 'react';
import { useLogger } from '#/logger';
import { type FeatureFlag, validateFeatureFlag } from '../flags';
import type { FeatureFlagPayload, FlaggrRelease } from '../types';
import { usePostHogFeatureFlag } from './use-posthog-feature-flag';

export interface UseFeatureFlagHook<F extends FeatureFlag> {
  /**
   * A boolean indicating if the feature flag is enabled.
   */
  enabled: boolean;

  /**
   * A boolean indicating if the feature flag is loading.
   */
  loading: boolean;

  /**
   * The feature flag release, indicating the stability of the feature flag.
   */
  release: FlaggrRelease | null;

  /**
   * The feature flag payload, containing additional data about the feature
   */
  payload: FeatureFlagPayload<F> | null;
}

/**
 * A hook to check if a feature flag is enabled.
 *
 * @param feature A feature flag to check.
 * @returns A boolean indicating if the feature flag is enabled.
 */
export const useFeatureFlag = <F extends FeatureFlag>(
  feature: F
): UseFeatureFlagHook<F> => {
  const result = usePostHogFeatureFlag(feature);
  const logger = useLogger();

  if (!validateFeatureFlag(feature)) {
    logger.error(`Invalid feature flag: ${feature}`);
  }

  return React.useMemo(() => {
    const loading = result.enabled === undefined;

    return {
      enabled: result.enabled === true,
      loading,
      release: !loading ? (result?.release ?? 'stable') : null,
      payload: result.payload,
    };
  }, [result]);
};
